import React, { useMemo, useCallback, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, matchPath, useParams, useLocation } from 'react-router-dom';
import { Modal } from '@miroculus/nucleo';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { saveProtocol } from 'reduxModules/protocols';
import { getOrganizationsAndTeamsList } from 'reduxModules/organizations/selectors';
import { toast } from 'react-toastify';
import { copyProtocolUrl, editProtocolUrl } from 'cons/routes';
import { saveProtocolUrl } from '../../constants/routes';
import ProtocolCreationForm, { CREATING, EDITING, COPYING } from './ProtocolCreationForm';
import { buildLocationOptions } from './locationOptions';

function getProtocolOperation(isCopyUrl, id) {
  if (isCopyUrl) return COPYING;
  return id ? EDITING : CREATING;
}

// Modal titles indexed by protocol operation
const MODAL_TITLES = ['Create protocol', 'Edit protocol', 'Copy protocol'];

const Schema = Yup.object().shape({
  name: Yup.string().required('Please enter the protocol name'),
  type: Yup.number().required('Please select the protocol type'),
  description: Yup.string(),
  visibility: Yup.string().required('Please select the protocol visibility'),
  teamId: Yup.string(),
});

export function ProtocolCreation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { teamId: teamIdParam, id: protocolId } = useParams();

  const isCopyUrl = matchPath(
    { path: copyProtocolUrl(':teamId', ':id'), end: false },
    pathname
  );
  const isNewUrl = matchPath(
    { path: saveProtocolUrl(':teamId', 'new'), end: false },
    pathname
  );

  // Selectors
  const currentProtocol = useSelector((state) => state.protocols.currentProtocol);
  const protocolTypes = useSelector((state) => state.protocols.protocolTypes);
  const allOrganizations = useSelector(getOrganizationsAndTeamsList);

  const {
    name = '',
    description = '',
    type,
    visibility,
    team,
    id,
  } = currentProtocol;

  const types = protocolTypes;

  // Compute personalTeam and organizations
  const { personalTeam, organizations } = useMemo(() => {
    let foundPersonalTeam;
    const filteredOrgs = allOrganizations?.filter((currOrganization) => {
      const personalTeamFound = currOrganization?.teams?.find(
        ({ name: teamName }) => teamName === 'Personal Workspace'
      );

      if (personalTeamFound) {
        foundPersonalTeam = personalTeamFound;
        return false;
      }
      return true;
    }) || [];

    return { personalTeam: foundPersonalTeam, organizations: filteredOrgs };
  }, [allOrganizations]);

  // The teamId is defined in the URL when copying a protocol
  const teamId = isCopyUrl || isNewUrl ? Number(teamIdParam) : team;

  const locationOptions = useMemo(
    () => buildLocationOptions(organizations, teamId, personalTeam),
    [organizations, teamId, personalTeam]
  );

  const { errors, status, handleSubmit, getFieldProps, setFieldValue } =
    useFormik({
      initialValues: {
        name,
        type,
        description,
        visibility,
        teamId,
      },
      onSubmit: async (values, actions) => {
        actions.setStatus(undefined);

        try {
          await dispatch(
            saveProtocol({
              ...values,
              protocolCopyFromId: protocolId !== 'new' ? protocolId : null,
              project: 1,
            })
          );
        } catch (e) {
          // We assume `uniqueness` error in the API means that the name already exists
          // We should update this once the API gets better at error reporting
          if (e.message.includes('uniqueness')) {
            actions.setStatus({
              name: 'Name already exists',
            });
          } else {
            toast.error(e.message);
          }
        }
      },
      validationSchema: Schema,
    });

  const handleClose = useCallback(() => {
    if (isCopyUrl) {
      navigate(-1);
    } else {
      navigate(editProtocolUrl(teamId, id));
    }
  }, [isCopyUrl, navigate, teamId, id]);

  const protocolOperation = getProtocolOperation(isCopyUrl, id);

  const label =
    protocolOperation === COPYING ? 'CopyProtocol' : 'CreateProtocol';

  const title = MODAL_TITLES[protocolOperation];

  return (
    <Modal label={label} onClose={handleClose}>
      <h1>{title}</h1>
      <ProtocolCreationForm
        status={status}
        errors={errors}
        getFieldProps={getFieldProps}
        handleSubmit={handleSubmit}
        setFieldValue={setFieldValue}
        locationOptions={locationOptions}
        protocolOperation={protocolOperation}
        handleClose={handleClose}
        types={types}
      />
    </Modal>
  );
};

export default memo(ProtocolCreation);
